import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });
        
        const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Show error toast for certain errors (but not for expected 404s from pipeline/watchlist checks)
    const skipToastUrls = ['/pipeline/opportunities/', '/watchlist/opportunities/'];
    const shouldSkipToast = skipToastUrls.some(url => originalRequest.url?.includes(url)) && error.response?.status === 404;
    
    if (error.response?.status !== 401 && !shouldSkipToast) {
      const errorData = (error.response?.data as any)?.error;
      const message = typeof errorData === 'string' 
        ? errorData 
        : (errorData?.message || 'An error occurred');
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  signup: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string; companyName?: string }) =>
    api.post('/auth/signup', data),
  
  verifyEmail: (data: { email: string; otp: string }) =>
    api.post('/auth/verify-email', data),
  
  resendVerification: (data: { email: string }) =>
    api.post('/auth/resend-verification', data),
  
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  
  verify2FA: (data: { tempToken: string; otp: string }) =>
    api.post('/auth/verify-2fa', data),
  
  forgotPassword: (data: { email: string }) =>
    api.post('/auth/forgot-password', data),
  
  resetPassword: (data: { email: string; otp: string; newPassword: string }) =>
    api.post('/auth/reset-password', data),
  
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  logout: (data?: { allDevices?: boolean }) =>
    api.post('/auth/logout', data),
  
  enable2FA: () =>
    api.post('/auth/2fa/enable'),
  
  confirm2FA: (data: { code: string }) =>
    api.post('/auth/2fa/confirm', data),
  
  disable2FA: (data: { password: string }) =>
    api.post('/auth/2fa/disable', data),
};

// User API
export const userApi = {
  getMe: () => api.get('/users/me'),
  updateMe: (data: any) => api.patch('/users/me', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/users/change-password', data),
  getNotifications: () => api.get('/users/notifications'),
  markNotificationRead: (id: string) => api.patch(`/users/notifications/${id}/read`, {}),
  getApiKeys: () => api.get('/users/api-keys'),
};

// Subscription API
export const subscriptionApi = {
  getSubscription: () => api.get('/subscriptions'),
  getPlans: () => api.get('/subscriptions/plans'),
  createSetupIntent: () => api.post('/subscriptions/setup-intent', {}),
  createSubscription: (data: { plan: string; paymentMethodId: string; isAnnual?: boolean }) =>
    api.post('/subscriptions', data),
  createCheckoutSession: (data: { plan: string; isAnnual?: boolean }) =>
    api.post('/subscriptions/checkout', data),
  cancelSubscription: (data?: { immediate?: boolean }) =>
    api.post('/subscriptions/cancel', data),
  updateSubscription: (data: { plan: string }) =>
    api.patch('/subscriptions', data),
  getPortalSession: () => api.post('/subscriptions/portal-session', {}),
};

// Opportunities API
export const opportunityApi = {
  search: (params?: any) => api.get('/opportunities/search', { params }),
  getById: (id: string) => api.get(`/opportunities/${id}`),
  getSummary: (id: string) => api.get(`/opportunities/${id}/summary`, { timeout: 120000 }),
  getNLPSummary: (id: string) => api.get(`/opportunities/${id}/nlp-summary`),
  save: (id: string, data: any) => api.post(`/opportunities/${id}/save`, data),
  getRecommendations: () => api.get('/opportunities/recommendations/list'),
};

// AI API
export const aiApi = {
  chat: (data: { message: string; conversationId?: string; context?: any; history?: any[] }) =>
    api.post('/ai/chat', data),
  getConversations: () => api.get('/ai/chat/conversations'),
  getConversation: (id: string) => api.get(`/ai/chat/conversations/${id}`),
  deleteConversation: (id: string) => api.delete(`/ai/chat/conversations/${id}`),
  generateProposal: (data: any) => api.post('/ai/proposal/generate', data),
  analyze: (data: any) => api.post('/ai/analyze', data),
  uploadDocument: (formData: FormData) =>
    api.post('/ai/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  queryDocuments: (data: { query: string }) => api.post('/ai/documents/query', data),
  saveDocumentFromOpportunity: (data: {
    documentUrl: string;
    filename: string;
    noticeId?: string;
    solicitationNumber?: string;
    title?: string;
    agency?: string;
    source?: string;
  }) => api.post('/ai/documents/save-from-opportunity', data),
  getDocuments: () => api.get('/ai/documents'),
  checkOpportunityDocuments: (noticeId: string) => 
    api.get(`/ai/documents/check-opportunity/${noticeId}`),
};

// CRM API
export const crmApi = {
  getData: () => api.get('/crm'),
  createPipeline: (data: any) => api.post('/crm/pipelines', data),
  addOpportunity: (pipelineId: string, data: any) =>
    api.post(`/crm/pipelines/${pipelineId}/opportunities`, data),
  updateOpportunityStage: (id: string, data: { stage: string; notes?: string }) =>
    api.patch(`/crm/opportunities/${id}/stage`, data),
  getContacts: (params?: any) => api.get('/crm/contacts', { params }),
  addContact: (data: any) => api.post('/crm/contacts', data),
  getDashboard: () => api.get('/crm/dashboard'),
};

// Integration API
export const integrationApi = {
  getAll: () => api.get('/integrations'),
  connectGSA: (data: { apiKey: string }) => api.post('/integrations/gsa', data),
  connectSeaPort: (data: { credentials: any }) => api.post('/integrations/seaport', data),
  connectZapier: () => api.post('/integrations/zapier', {}),
  connectSalesforce: (data: any) => api.post('/integrations/salesforce', data),
  disconnect: (id: string) => api.delete(`/integrations/${id}`),
};

// Team API
export const teamApi = {
  getAll: () => api.get('/teams'),
  create: (data: any) => api.post('/teams', data),
  inviteMember: (teamId: string, data: { email: string; role: string }) =>
    api.post(`/teams/${teamId}/invite`, data),
  updateMemberRole: (teamId: string, memberId: string, data: { role: string }) =>
    api.patch(`/teams/${teamId}/members/${memberId}/role`, data),
  removeMember: (teamId: string, memberId: string) =>
    api.delete(`/teams/${teamId}/members/${memberId}`),
};

// Agent API (OpenClaw Integration)
export const agentApi = {
  // Agent Types
  getTypes: () => api.get('/agents/types'),
  getHealth: () => api.get('/agents/health'),
  
  // Task Management
  createTask: (data: { agentType: string; payload: any; priority?: string }) =>
    api.post('/agents/tasks', data),
  getTask: (id: string) => api.get(`/agents/tasks/${id}`),
  getTaskResult: (id: string) => api.get(`/agents/tasks/${id}/result`),
  cancelTask: (id: string) => api.delete(`/agents/tasks/${id}`),
  getMyTasks: (params?: { status?: string; limit?: number }) =>
    api.get('/agents/tasks', { params }),
};

// Pipeline API (Opportunity Pipeline)
export const pipelineApi = {
  // Opportunities in Pipeline
  getOpportunities: (params?: { stage?: string; assignedToMe?: boolean }) =>
    api.get('/pipeline/opportunities', { params }),
  getOpportunity: (id: string) => api.get(`/pipeline/opportunities/${id}`),
  getByOpportunityId: (opportunityId: string) => api.get(`/pipeline/opportunities/${opportunityId}`),
  addOpportunity: (opportunityId: string) => api.post(`/pipeline/opportunities/${opportunityId}`, {}),
  updateStage: (id: string, data: { stage: string; notes?: string }) =>
    api.post(`/pipeline/opportunities/${id}/stage`, data),
  analyze: (id: string) => api.post(`/pipeline/opportunities/${id}/analyze`),
  matchVendors: (id: string) => api.post(`/pipeline/opportunities/${id}/match`),
  
  // Configuration
  getConfig: () => api.get('/pipeline/config'),
  updateConfig: (data: any) => api.put('/pipeline/config', data),
  
  // Scoring
  calculateMatch: (id: string) => api.post(`/pipeline/opportunities/${id}/calculate-match`),
};

// Watch List API
export const watchListApi = {
  // Get user's watch list
  getAll: (params?: { limit?: number; offset?: number }) =>
    api.get('/watchlist', { params }),
  
  // Get watch list stats
  getStats: () => api.get('/watchlist/stats'),
  
  // Check if opportunity is in watch list
  checkStatus: (opportunityId: string) =>
    api.get(`/watchlist/opportunities/${opportunityId}/check`),
  
  // Add opportunity to watch list
  add: (opportunityId: string, data?: { notes?: string; alertOnUpdate?: boolean }) =>
    api.post(`/watchlist/opportunities/${opportunityId}`, data || {}),
  
  // Remove opportunity from watch list
  remove: (opportunityId: string) =>
    api.delete(`/watchlist/opportunities/${opportunityId}`),
  
  // Update watch list item
  update: (watchListId: string, data: { notes?: string; alertOnUpdate?: boolean }) =>
    api.patch(`/watchlist/${watchListId}`, data),
  
  // Toggle alerts
  toggleAlerts: (opportunityId: string, enabled: boolean) =>
    api.post(`/watchlist/opportunities/${opportunityId}/alerts`, { enabled }),
};

// Documents API
export const documentApi = {
  // Get filename for a single document URL
  getFilename: (url: string) =>
    api.get('/documents/filename', { params: { url } }),
  
  // Get filenames for multiple document URLs (batch)
  getFilenames: (urls: string[]) =>
    api.post('/documents/filenames', { urls }),
};

// Orchestrator API (Workflows)
export const orchestratorApi = {
  // Workflows
  getWorkflows: () => api.get('/orchestrator/workflows'),
  createWorkflow: (data: { name: string; description?: string; definition: any }) =>
    api.post('/orchestrator/workflows', data),
  getWorkflow: (id: string) => api.get(`/orchestrator/workflows/${id}`),
  executeWorkflow: (id: string, context?: any) =>
    api.post(`/orchestrator/workflows/${id}/execute`, { context }),
  deleteWorkflow: (id: string) => api.delete(`/orchestrator/workflows/${id}`),
  
  // Agent Chains
  createChain: (data: { name: string; description?: string; steps: any[] }) =>
    api.post('/orchestrator/chains', data),
  executeChain: (id: string, initialInput?: any) =>
    api.post(`/orchestrator/chains/${id}/execute`, { initialInput }),
  
  // Executions
  getExecution: (id: string) => api.get(`/orchestrator/executions/${id}`),
  getExecutions: (params?: { workflowId?: string; status?: string }) =>
    api.get('/orchestrator/executions', { params }),
};

// Analytics API
export const analyticsApi = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getAgentPerformance: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/agents/performance', { params }),
  getOpportunityReport: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/reports/opportunities', { params }),
  getAgentReport: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/reports/agents', { params }),
  exportReport: (type: string, format: 'csv' | 'json', params?: any) =>
    api.get('/analytics/reports/export', { 
      params: { type, format, ...params },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),
};

// Collaboration API
export const collaborationApi = {
  // Comments
  getComments: (opportunityId: string) =>
    api.get(`/collaboration/opportunities/${opportunityId}/comments`),
  addComment: (opportunityId: string, data: { content: string; parentId?: string; mentions?: string[] }) =>
    api.post(`/collaboration/opportunities/${opportunityId}/comments`, data),
  resolveComment: (opportunityId: string, commentId: string) =>
    api.post(`/collaboration/opportunities/${opportunityId}/comments/${commentId}/resolve`),
  
  // Assignments
  assignOpportunity: (opportunityId: string, data: { userId: string; role: string }) =>
    api.post(`/collaboration/opportunities/${opportunityId}/assign`, data),
  unassignOpportunity: (opportunityId: string, userId: string) =>
    api.delete(`/collaboration/opportunities/${opportunityId}/assign/${userId}`),
  getMyAssignments: () => api.get('/collaboration/assigned'),
  
  // Activity Feed
  getActivityFeed: (opportunityId: string, params?: { limit?: number; before?: string }) =>
    api.get(`/collaboration/opportunities/${opportunityId}/activity`, { params }),
  
  // Shared Opportunities
  getSharedOpportunities: () => api.get('/collaboration/shared'),
};

// Email API
export const emailApi = {
  createRelay: (data: { name: string; purpose?: string; forwardTo?: string }) =>
    api.post('/emails/relay', data),
  getInbox: (params?: { status?: string; limit?: number }) =>
    api.get('/emails/inbox', { params }),
  getEmail: (id: string) => api.get(`/emails/inbox/${id}`),
};

export default api;
